// apps/web/src/components/disputes/DisputesManager.tsx
// Disputes management component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Dispute } from '../../types/dispute';
import { apiClient } from '../../lib/apiClient';

export function DisputesManager() {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const fetchDisputes = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/disputes');
        setDisputes(response.data.disputes);
      } catch (err) {
        setError('Failed to load disputes');
        console.error('Error loading disputes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDisputes();
  }, []);

  const handleCreateDispute = async (disputeData: any) => {
    try {
      const response = await apiClient.post('/api/v1/disputes', disputeData);
      setDisputes([response.data, ...disputes]);
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create dispute');
      console.error('Error creating dispute:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading disputes...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="disputes-manager">
      <div className="header">
        <h2>Disputes Management</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary"
        >
          {showCreateForm ? 'Cancel' : 'Create Dispute'}
        </button>
      </div>

      {showCreateForm && (
        <DisputeForm 
          onSubmit={handleCreateDispute} 
          onCancel={() => setShowCreateForm(false)} 
        />
      )}

      <div className="disputes-grid">
        {disputes.map(dispute => (
          <DisputeCard key={dispute.id} dispute={dispute} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function DisputeCard({ dispute }: { dispute: Dispute }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'status-open';
      case 'IN_PROGRESS': return 'status-in-progress';
      case 'ESCALATED': return 'status-escalated';
      case 'RESOLVED': return 'status-resolved';
      case 'CLOSED': return 'status-closed';
      default: return 'status-default';
    }
  };

  return (
    <div className="dispute-card">
      <div className="dispute-header">
        <h3>{dispute.title}</h3>
        <span className={`status-badge ${getStatusColor(dispute.status)}`}>
          {dispute.status}
        </span>
      </div>
      <p>{dispute.description || 'No description'}</p>
      <p><strong>Participants:</strong> {dispute.participants.length}</p>
      <p><strong>Type:</strong> {dispute.disputeType}</p>
      <p><strong>Created:</strong> {new Date(dispute.createdAt).toLocaleDateString()}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-warning">Mediate</button>
        <button className="btn btn-primary">Escalate</button>
      </div>
    </div>
  );
}

// Helper component
function DisputeForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    disputeType: 'CONTRACT',
    participants: [] as string[],
    status: 'OPEN'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="dispute-form">
      <h3>Create New Dispute</h3>
      <input
        type="text"
        placeholder="Dispute Title"
        value={formData.title}
        onChange={(e) => setFormData({...formData, title: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <select
        value={formData.disputeType}
        onChange={(e) => setFormData({...formData, disputeType: e.target.value})}
      >
        <option value="CONTRACT">Contract</option>
        <option value="PAYMENT">Payment</option>
        <option value="CONTENT">Content</option>
        <option value="CREATOR">Creator</option>
        <option value="CAMPAIGN">Campaign</option>
        <option value="OTHER">Other</option>
      </select>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Dispute</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}